import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ContactResponse,
  SubmissionListResponse,
  SubmissionResponse,
  SubmissionStatus,
} from "@guestbook/shared";
import { apiFetch, type ApiClientError } from "@/shared/lib/api";

const SUBMISSIONS_KEY = "submissions" as const;

export interface SubmissionsListParams {
  status?: SubmissionStatus;
  cursor?: string;
  limit?: number;
}

function buildSubmissionsQuery(params: SubmissionsListParams): string {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.limit) search.set("limit", String(params.limit));
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function useSubmissions(params: SubmissionsListParams = {}) {
  return useQuery<SubmissionListResponse, ApiClientError>({
    queryKey: [SUBMISSIONS_KEY, "list", params],
    queryFn: () =>
      apiFetch<SubmissionListResponse>(`/submissions${buildSubmissionsQuery(params)}`),
  });
}

/**
 * Approve a submission. With `contactId` it merges into that existing contact;
 * without, it creates a new contact. Invalidates the contacts + households
 * caches so the approved contact shows up immediately.
 */
export function useApproveSubmission() {
  const qc = useQueryClient();
  return useMutation<
    ContactResponse,
    ApiClientError,
    { id: string; contactId?: string | null }
  >({
    mutationFn: ({ id, contactId }) =>
      apiFetch<ContactResponse>(`/submissions/${id}/approve`, {
        method: "POST",
        body: JSON.stringify(contactId ? { contactId } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SUBMISSIONS_KEY, "list"] });
      qc.invalidateQueries({ queryKey: ["contacts", "list"] });
      qc.invalidateQueries({ queryKey: ["households"] });
    },
  });
}

export function useRejectSubmission() {
  const qc = useQueryClient();
  return useMutation<SubmissionResponse, ApiClientError, string>({
    mutationFn: (id) =>
      apiFetch<SubmissionResponse>(`/submissions/${id}/reject`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SUBMISSIONS_KEY, "list"] });
    },
  });
}
